import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Switch,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  Add,
  Print,
  Delete,
  ArrowBack,
  Edit,
  Search,
  Close as CloseIcon,
  AttachMoney,
  GetApp,
} from "@mui/icons-material";

interface Pasajero {
  id: number;
  nombre: string;
  dni: string;
}

interface Pago {
  id: number;
  observacion: string;
  importe: number;
  moneda: string;
  created_at?: string;
  responsable_nombre?: string;
  revisado_por_nombre?: string;
  fecha_revision?: string;
}

interface SenaPasajerosProps {
  open: boolean;
  onClose: () => void;
  producto: any;
  onUpdateMontoSena: (nuevoMonto: number) => void;
  onAddPagoPasajero: (pasajero: Pasajero, pagoData: { importe: number; observacion: string; moneda: string; fecha?: string }) => Promise<void>;
  onUpdatePagoPasajero: (pagoId: number, pagoData: { importe: number; observacion: string; fecha?: string }) => Promise<void>;
  onDeletePago: (id: number) => Promise<void>;
  onToggleRevisado?: (id: number, revisado: boolean, fechaRevisado?: string) => Promise<void>;
}

export const SenaPasajeros: React.FC<SenaPasajerosProps> = ({
  open,
  onClose,
  producto,
  onUpdateMontoSena,
  onAddPagoPasajero,
  onUpdatePagoPasajero,
  onDeletePago,
  onToggleRevisado,
}) => {
  const [terminoBusqueda, setTerminoBusqueda] = useState("");
  const [editandoMonto, setEditandoMonto] = useState(false);
  const [nuevoMontoSena, setNuevoMontoSena] = useState<string>("");

  // Estado para el detalle de pagos del pasajero seleccionado
  const [pasajeroEnDetalle, setPasajeroEnDetalle] = useState<any>(null);
  const [modalPagoAbierto, setModalPagoAbierto] = useState(false);
  const [pagoAEditar, setPagoAEditar] = useState<any>(null);
  const [pagoData, setPagoData] = useState({
    monto: "",
    condicion: "Efectivo",
    percepcion: "",
    tipoCambio: "1420",
    concepto: "EFECTIVO",
    fecha: dayjs() as Dayjs | null
  });

  // Obtener el monto de la seña desde la metadata del producto
  const montoSena = useMemo(() => {
    try {
      if (producto?.observaciones) {
        const obs = Array.isArray(producto.observaciones) 
          ? producto.observaciones 
          : JSON.parse(producto.observaciones || "[]");
        const metadata = obs.find((o: any) => o.descripcion?.includes("METADATA_GRUPAL"));
        if (metadata) {
          const data = JSON.parse(metadata.descripcion.replace("METADATA_GRUPAL: ", ""));
          return data.montoSena || 0;
        }
      }
    } catch (e) {}
    return 0;
  }, [producto]);

  const handleEditMonto = () => {
    setNuevoMontoSena(montoSena.toString());
    setEditandoMonto(true);
  };

  const handleSaveMonto = () => {
    const valor = parseFloat(nuevoMontoSena);
    if (!isNaN(valor)) {
      onUpdateMontoSena(valor);
    }
    setEditandoMonto(false);
  };

  // Calcular el saldo de cada pasajero para la tabla principal
  const pasajerosConSaldo = useMemo(() => {
    const pasajeros = producto?.pasajerosLista || [];
    const pagos = producto?.pagos || [];

    return pasajeros.map((pasajero: Pasajero) => {
      const pagosPasajero = pagos.filter((p: Pago) => 
        p.observacion?.includes(`[PASAJERO:${pasajero.dni}]`) &&
        p.observacion.toUpperCase().includes("SEÑA")
      );
      const totalPagado = pagosPasajero.reduce((acc: number, p: Pago) => acc + (p.importe || 0), 0);
      // El saldo individual sigue siendo respecto a la seña
      const saldo = Math.max(0, montoSena - totalPagado);
      return { ...pasajero, saldo, totalPagado };
    });
  }, [producto, montoSena]);

  // Nuevo cálculo: Saldo Total del Paquete (Precio Unitario * Pasajeros - Pagos)
  const resumenGeneral = useMemo(() => {
    const precioUnitario = producto?.precioVenta || 0;
    const cantPasajeros = producto?.pasajerosLista?.length || 0;
    const totalVentaCosto = precioUnitario * cantPasajeros;
    const totalPagado = (producto?.pagos || []).reduce((acc: number, p: any) => acc + (p.importe || 0), 0);
    
    return {
      totalVentaCosto,
      totalPagado,
      saldoRestante: Math.max(0, totalVentaCosto - totalPagado)
    };
  }, [producto]);

  const showExtra = useMemo(() => {
    const cat = (producto?.categoria || "").toLowerCase();
    return ["quince", "educativos", "otros"].includes(cat);
  }, [producto]);

  // Filtrar pagos del pasajero actual en detalle
  const pagosPasajeroActual = useMemo(() => {
    if (!pasajeroEnDetalle) return [];
    return (producto?.pagos || []).filter((p: any) => 
      p.observacion?.includes(`[PASAJERO:${pasajeroEnDetalle.dni}]`) &&
      p.observacion.toUpperCase().includes("SEÑA")
    );
  }, [producto, pasajeroEnDetalle]);

  const resumenPagosPasajero = useMemo(() => {
    const pagado = pagosPasajeroActual.reduce((acc: number, p: any) => acc + (p.importe || 0), 0);
    return {
      pagado,
      monto: montoSena,
      saldo: Math.max(0, montoSena - pagado)
    };
  }, [pagosPasajeroActual, montoSena]);

  const handleAbirDetalle = (pasajero: any) => {
    setPasajeroEnDetalle(pasajero);
  };

  const handleAbrirModalPago = (pago?: any) => {
    if (pago) {
      setPagoAEditar(pago);
      const obs = pago.observacion || "";
      setPagoData({
        monto: pago.importe.toString(),
        condicion: obs.match(/\[CONDICION:\s*(.*?)\]/)?.[1] || "Efectivo",
        percepcion: obs.match(/\[PERV:\s*(.*?)\]/)?.[1] || "",
        tipoCambio: obs.match(/\[TC:\s*(.*?)\]/)?.[1] || "1420",
        concepto: obs.replace(/\[.*?\]/g, "").trim(),
        fecha: dayjs(pago.created_at || new Date())
      });
    } else {
      setPagoAEditar(null);
      setPagoData({
        monto: "",
        condicion: "Efectivo",
        percepcion: "",
        tipoCambio: "1420",
        concepto: "EFECTIVO",
        fecha: dayjs()
      });
    }
    setModalPagoAbierto(true);
  };

  const handleGuardarPagoInterno = async () => {
    if (!pasajeroEnDetalle) return;
    const monto = parseFloat(pagoData.monto);
    if (isNaN(monto)) return;

    let observacion = `[CONDICION: ${pagoData.condicion}] ${pagoData.concepto} [PASAJERO:${pasajeroEnDetalle.dni}] [SEÑA]`;
    if (pagoData.percepcion) observacion += ` [PERV:${pagoData.percepcion}]`;
    if (pagoData.tipoCambio) observacion += ` [TC:${pagoData.tipoCambio}]`;

    const data = {
      importe: monto,
      observacion: observacion,
      fecha: pagoData.fecha?.toISOString()
    };

    if (pagoAEditar) {
      await onUpdatePagoPasajero(pagoAEditar.id, data);
    } else {
      await onAddPagoPasajero(pasajeroEnDetalle, { ...data, moneda: "ARS" });
    }
    
    setModalPagoAbierto(false);
  };

  const handleToggleRevisadoInterno = async (pagoId: number, checked: boolean) => {
    if (!onToggleRevisado) return;
    const fechaActual = checked ? dayjs().format('DD/MM/YYYY') : "";
    await onToggleRevisado(pagoId, checked, fechaActual);
  };

  const pasajerosFiltrados = pasajerosConSaldo.filter((p: any) =>
    p.nombre.toLowerCase().includes(terminoBusqueda.toLowerCase()) ||
    p.dni.includes(terminoBusqueda)
  );

  const handleExportExcel = () => {
    let content = "Nombre\tDNI\tTotal Pagado\tSaldo Se\u00f1a\n";
    pasajerosConSaldo.forEach((p: any) => {
      content += `${p.nombre}\t${p.dni}\t${p.totalPagado}\t${p.saldo}\n`;
    });
    
    const blob = new Blob([content], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sena_${producto?.concepto || 'paquete'}.xls`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrintWindow = () => {
    window.print();
  };

  const formatearMoneda = (val: number) => {
    const moneda = producto?.moneda === "USD" ? "U$S" : "$";
    return `${moneda} ${val.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onClose} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            SEÑA | {producto?.concepto?.toUpperCase()}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Barra Azul de Monto */}
        <Box sx={{ bgcolor: '#5ba4e5', color: 'white', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {editandoMonto ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField 
                    size="small" 
                    value={nuevoMontoSena} 
                    onChange={(e) => setNuevoMontoSena(e.target.value)}
                    sx={{ bgcolor: 'white', borderRadius: 1, width: 150 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                  <Button variant="contained" color="success" size="small" onClick={handleSaveMonto}>Guardar</Button>
                  <Button variant="contained" color="error" size="small" onClick={() => setEditandoMonto(false)}>Cancelar</Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 'bold' }}>MONTO SEÑA</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                    {formatearMoneda(montoSena)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button color="inherit" startIcon={<Edit />} size="small" onClick={handleEditMonto}>Editar Seña</Button>
            <Button color="inherit" startIcon={<GetApp />} size="small" onClick={handleExportExcel}>Exportar a Excel</Button>
            <Button color="inherit" startIcon={<Print />} size="small" onClick={handlePrintWindow}>Imprimir</Button>
            <Button color="inherit" startIcon={<Delete />} size="small">Eliminar</Button>
          </Box>
        </Box>

        {/* Buscador */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            size="small"
            placeholder="Buscar en la tabla"
            value={terminoBusqueda}
            onChange={(e) => setTerminoBusqueda(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
        </Box>

        {/* Tabla de Pasajeros */}
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '150px' }}>Acciones</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Saldo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Apellido</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pasajerosFiltrados.map((pasajero: any) => {
                const parts = (pasajero.nombre || "").split(',');
                const apellido = parts[0]?.trim() || '';
                const nombre = parts[1]?.trim() || pasajero.nombre;

                return (
                  <TableRow key={pasajero.id} hover>
                    <TableCell>
                      <Tooltip title="Ver Detalle de Pagos">
                        <IconButton 
                          size="small" 
                          sx={{ color: '#461264' }}
                          onClick={() => handleAbirDetalle(pasajero)}
                        >
                          <AttachMoney />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {formatearMoneda(pasajero.saldo)}
                    </TableCell>
                    <TableCell>{apellido}</TableCell>
                    <TableCell>{nombre}</TableCell>
                  </TableRow>
                );
              })}
              {pasajerosFiltrados.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No se encontraron pasajeros</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* MODAL DETALLE DE PAGOS PASAJERO */}
      <Dialog 
        open={Boolean(pasajeroEnDetalle)} 
        onClose={() => setPasajeroEnDetalle(null)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee' }}>
          <Typography sx={{ color: '#666', fontSize: '1.1rem' }}>
            Pagos | <span style={{ fontWeight: 'bold', color: '#333' }}>{(pasajeroEnDetalle?.nombre || "").toUpperCase()}</span> | SEÑA
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                startIcon={<Add />} 
                size="small" 
                sx={{ bgcolor: '#5ba4e5', textTransform: 'none' }}
                onClick={() => handleAbrirModalPago()}
              >
                Agregar Nuevo Pago
              </Button>
            <Button 
              variant="contained" 
              startIcon={<Print />} 
              size="small" 
              sx={{ bgcolor: '#55c2d5', textTransform: 'none' }}
            >
              Imprimir Ficha
            </Button>
            <IconButton onClick={() => setPasajeroEnDetalle(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>N°</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Monto pago</TableCell>
                  {showExtra && (
                    <>
                      <TableCell sx={{ fontWeight: "bold" }}>Percepcion</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>TC</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Total U$D</TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontWeight: "bold" }}>Fecha de pago</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Concepto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Responsable</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sucursal</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Revisado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Revisado por</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha de revisión</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagosPasajeroActual.map((p: any, idx: number) => (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <IconButton size="small" sx={{ color: '#5ba4e5', p: 0.5 }} onClick={() => handleAbrirModalPago(p)}><Edit fontSize="inherit" /></IconButton>
                        <IconButton size="small" sx={{ color: '#5ba4e5', p: 0.5 }} onClick={() => onDeletePago(p.id)}><CloseIcon fontSize="inherit" /></IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{formatearMoneda(p.importe)}</TableCell>
                    {showExtra && (
                      <>
                        <TableCell>{(p.observacion || "").match(/\[PERV:\s*(.*?)\]/)?.[1] || "-"}</TableCell>
                        <TableCell>{(p.observacion || "").match(/\[TC:\s*(.*?)\]/)?.[1] || "-"}</TableCell>
                        <TableCell>
                          {(() => {
                            const tc = parseFloat((p.observacion || "").match(/\[TC:\s*(.*?)\]/)?.[1] || "0");
                            const perv = parseFloat((p.observacion || "").match(/\[PERV:\s*(.*?)\]/)?.[1] || "0");
                            const totalPesos = (p.importe || 0) + perv;
                            return tc > 0 ? `U$D ${(totalPesos / tc).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-";
                          })()}
                        </TableCell>
                      </>
                    )}
                    <TableCell>{new Date(p.created_at || Date.now()).toLocaleDateString('es-AR')}</TableCell>
                    <TableCell>{(p.observacion || "").replace(/\[.*?\]/g, "").trim() || "-"}</TableCell>
                    <TableCell>{p.responsable_nombre || '-'}</TableCell>
                    <TableCell>01_ESTUDIANTIL</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: p.revisado_por_nombre ? '#4caf50' : '#f44336',
                        borderRadius: 1,
                        px: 0.5,
                        width: 'fit-content',
                        mx: 'auto'
                      }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', mr: 0.5 }}>
                          {p.revisado_por_nombre ? 'YES' : 'NO'}
                        </Typography>
                        <Switch 
                          size="small" 
                          checked={!!p.revisado_por_nombre}
                          onChange={(e) => handleToggleRevisadoInterno(p.id, e.target.checked)}
                          sx={{ 
                            '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'white' },
                            width: 34, height: 20, p: 0,
                            '& .MuiSwitch-thumb': { width: 14, height: 14, mt: 0.4, ml: 0.4 }
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{p.revisado_por_nombre || '-'}</TableCell>
                    <TableCell>{p.fecha_revision || '-'}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>Pagado</TableCell>
                  <TableCell colSpan={1}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{formatearMoneda(resumenPagosPasajero.pagado)}</TableCell>
                  {showExtra && <TableCell colSpan={3}></TableCell>}
                  <TableCell colSpan={7}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* CUADROS DE RESUMEN INFERIOR */}
          <Box sx={{ mt: 3, width: 300 }}>
            <Table size="small" sx={{ border: '1px solid #eee' }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#eee', fontWeight: 'bold', width: '40%' }}>Monto</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>{formatearMoneda(resumenPagosPasajero.monto)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#eee', fontWeight: 'bold' }}>Pagado</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>{formatearMoneda(resumenPagosPasajero.pagado)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#eee', fontWeight: 'bold' }}>Saldo</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>{formatearMoneda(resumenPagosPasajero.saldo)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
      </Dialog>

      {/* DIALOGO AGREGAR/EDITAR PAGO */}
      <Dialog open={modalPagoAbierto} onClose={() => setModalPagoAbierto(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', borderBottom: '1px solid #eee', py: 1 }}>
          <Typography variant="h6" sx={{ color: '#666' }}>{pagoAEditar ? 'Editar Pago' : 'Agregar Pago'}</Typography>
          <Typography variant="caption" sx={{ color: '#999' }}>Cambio del día U$S 1420 | €</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>Monto Total en Pesos</Typography>
              <TextField 
                fullWidth 
                size="small"
                type="number" 
                value={pagoData.monto} 
                onChange={(e) => setPagoData({...pagoData, monto: e.target.value})}
                autoFocus
              />
            </Box>
            
            {/* Condición de pago (Solo si showExtra) */}
            {showExtra && (
              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>Condición de pago</Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={pagoData.condicion} 
                  onChange={(e) => setPagoData({...pagoData, condicion: e.target.value})} 
                  SelectProps={{ native: true }}
                >
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="Tarjeta de crédito">Tarjeta de crédito</option>
                  <option value="Cheque">Cheque</option>
                </TextField>
              </Box>
            )}

            {/* Percepción (Solo si showExtra) */}
            {showExtra && (
              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>Percepción</Typography>
                <TextField 
                  fullWidth 
                  size="small"
                  type="number"
                  value={pagoData.percepcion} 
                  onChange={(e) => setPagoData({...pagoData, percepcion: e.target.value})}
                />
              </Box>
            )}

            {/* Tipo de cambio */}
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>Tipo de cambio</Typography>
              <TextField 
                fullWidth 
                size="small"
                type="number"
                value={pagoData.tipoCambio}
                onChange={(e) => setPagoData({...pagoData, tipoCambio: e.target.value})}
                disabled={!showExtra}
              />
            </Box>

            {/* Total en Dólares (Calculado) */}
            {showExtra && (
              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>Total en Dólares</Typography>
                <TextField 
                  fullWidth 
                  size="small"
                  value={(() => {
                    const monto = parseFloat(pagoData.monto) || 0;
                    const perv = parseFloat(pagoData.percepcion) || 0;
                    const tc = parseFloat(pagoData.tipoCambio) || 0;
                    if (tc > 0) return `U$D ${((monto + perv) / tc).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    return "-";
                  })()}
                  disabled
                />
              </Box>
            )}

            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>Concepto</Typography>
              <TextField 
                fullWidth 
                size="small"
                value={pagoData.concepto} 
                onChange={(e) => setPagoData({...pagoData, concepto: e.target.value.toUpperCase()})}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#eee', px: 2, py: 1.5, borderRight: '1px solid #ccc', minWidth: 150 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Fecha de pago</Typography>
              </Box>
              <Box sx={{ flex: 1, px: 1 }}>
                <DatePicker 
                  value={pagoData.fecha}
                  onChange={(val) => setPagoData({...pagoData, fecha: val})}
                  slotProps={{ 
                    textField: { 
                      variant: 'standard', 
                      fullWidth: true, 
                      InputProps: { disableUnderline: true } 
                    } 
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            onClick={handleGuardarPagoInterno} 
            sx={{ bgcolor: '#77c485', '&:hover': { bgcolor: '#66a36f' }, color: 'white', px: 4, fontWeight: 'bold' }}
          >
            GUARDAR
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
    </LocalizationProvider>
  );
};
